/**
 * API Connectors & Integrations
 * Manage external service connections
 */

export const CONNECTOR_TYPES = {
  // Developer Tools
  GITHUB: {
    id: 'github',
    name: 'GitHub',
    icon: '🐙',
    category: 'developer',
    description: 'GitHub API for repositories, issues, PRs',
    requiredFields: ['apiToken', 'username'],
    optionalFields: ['organization'],
    endpoints: {
      repos: 'https://api.github.com/user/repos',
      issues: 'https://api.github.com/repos/{owner}/{repo}/issues',
      prs: 'https://api.github.com/repos/{owner}/{repo}/pulls'
    }
  },

  GITLAB: {
    id: 'gitlab',
    name: 'GitLab',
    icon: '🦊',
    category: 'developer',
    description: 'GitLab API for projects, MRs, pipelines',
    requiredFields: ['apiToken', 'instanceUrl'],
    endpoints: {
      projects: '{instanceUrl}/api/v4/projects'
    }
  },

  // Communication
  SLACK: {
    id: 'slack',
    name: 'Slack',
    icon: '💬',
    category: 'communication',
    description: 'Slack API for messages and channels',
    requiredFields: ['botToken', 'workspaceId'],
    endpoints: {
      postMessage: 'https://slack.com/api/chat.postMessage',
      channels: 'https://slack.com/api/conversations.list'
    }
  },

  DISCORD: {
    id: 'discord',
    name: 'Discord',
    icon: '🎮',
    category: 'communication',
    description: 'Discord bot API',
    requiredFields: ['botToken'],
    endpoints: {
      sendMessage: 'https://discord.com/api/v10/channels/{channelId}/messages'
    }
  },

  // Databases
  SUPABASE: {
    id: 'supabase',
    name: 'Supabase',
    icon: '🔥',
    category: 'database',
    description: 'Supabase PostgreSQL and Auth',
    requiredFields: ['projectUrl', 'anonKey'],
    optionalFields: ['serviceRoleKey'],
    endpoints: {
      rest: '{projectUrl}/rest/v1',
      auth: '{projectUrl}/auth/v1'
    }
  },

  FIREBASE: {
    id: 'firebase',
    name: 'Firebase',
    icon: '🔥',
    category: 'database',
    description: 'Firebase Firestore and Auth',
    requiredFields: ['projectId', 'apiKey'],
    endpoints: {
      firestore: 'https://firestore.googleapis.com/v1/projects/{projectId}/databases/(default)/documents'
    }
  },

  MONGODB: {
    id: 'mongodb',
    name: 'MongoDB Atlas',
    icon: '🍃',
    category: 'database',
    description: 'MongoDB Atlas API',
    requiredFields: ['connectionString'],
    endpoints: {}
  },

  // AI Services
  OPENAI: {
    id: 'openai',
    name: 'OpenAI',
    icon: '🤖',
    category: 'ai',
    description: 'OpenAI API for GPT models',
    requiredFields: ['apiKey'],
    endpoints: {
      chat: 'https://api.openai.com/v1/chat/completions',
      embeddings: 'https://api.openai.com/v1/embeddings'
    }
  },

  ANTHROPIC: {
    id: 'anthropic',
    name: 'Anthropic',
    icon: '🧠',
    category: 'ai',
    description: 'Anthropic Claude API',
    requiredFields: ['apiKey'],
    endpoints: {
      messages: 'https://api.anthropic.com/v1/messages'
    }
  },

  PINECONE: {
    id: 'pinecone',
    name: 'Pinecone',
    icon: '🌲',
    category: 'ai',
    description: 'Pinecone vector database',
    requiredFields: ['apiKey', 'environment', 'indexName'],
    endpoints: {
      query: 'https://{indexName}-{projectId}.svc.{environment}.pinecone.io/query'
    }
  },

  // Cloud Platforms
  AWS: {
    id: 'aws',
    name: 'AWS',
    icon: '☁️',
    category: 'cloud',
    description: 'Amazon Web Services',
    requiredFields: ['accessKeyId', 'secretAccessKey', 'region'],
    endpoints: {}
  },

  VERCEL: {
    id: 'vercel',
    name: 'Vercel',
    icon: '▲',
    category: 'cloud',
    description: 'Vercel deployment API',
    requiredFields: ['apiToken'],
    endpoints: {
      deployments: 'https://api.vercel.com/v13/deployments'
    }
  },

  RAILWAY: {
    id: 'railway',
    name: 'Railway',
    icon: '🚂',
    category: 'cloud',
    description: 'Railway deployment platform',
    requiredFields: ['apiToken', 'projectId'],
    endpoints: {
      graphql: 'https://backboard.railway.app/graphql/v2'
    }
  },

  // Productivity
  NOTION: {
    id: 'notion',
    name: 'Notion',
    icon: '📝',
    category: 'productivity',
    description: 'Notion API for pages and databases',
    requiredFields: ['apiToken'],
    endpoints: {
      search: 'https://api.notion.com/v1/search',
      databases: 'https://api.notion.com/v1/databases/{databaseId}/query'
    }
  },

  AIRTABLE: {
    id: 'airtable',
    name: 'Airtable',
    icon: '📊',
    category: 'productivity',
    description: 'Airtable API',
    requiredFields: ['apiKey', 'baseId'],
    endpoints: {
      records: 'https://api.airtable.com/v0/{baseId}/{tableName}'
    }
  }
};

export const CONNECTOR_CATEGORIES = {
  developer: { name: 'Developer Tools', icon: '💻' },
  communication: { name: 'Communication', icon: '💬' },
  database: { name: 'Databases', icon: '🗄️' },
  ai: { name: 'AI Services', icon: '🤖' },
  cloud: { name: 'Cloud Platforms', icon: '☁️' },
  productivity: { name: 'Productivity', icon: '📊' }
};

export class ConnectorManager {
  constructor() {
    this.connections = new Map();
    this.loadConnections();
  }

  /**
   * Load saved connections from localStorage
   */
  loadConnections() {
    try {
      const saved = localStorage.getItem('api-connectors');
      if (saved) {
        const data = JSON.parse(saved);
        this.connections = new Map(Object.entries(data));
      }
    } catch (error) {
      console.error('[Connectors] Load error:', error);
    }
  }

  /**
   * Save connections to localStorage
   */
  saveConnections() {
    try {
      const data = Object.fromEntries(this.connections);
      localStorage.setItem('api-connectors', JSON.stringify(data));
    } catch (error) {
      console.error('[Connectors] Save error:', error);
    }
  }

  /**
   * Add or update a connection
   */
  addConnection(connectorId, credentials) {
    const connector = CONNECTOR_TYPES[connectorId.toUpperCase()];
    if (!connector) {
      throw new Error(`Unknown connector: ${connectorId}`);
    }

    // Validate required fields
    for (const field of connector.requiredFields) {
      if (!credentials[field]) {
        throw new Error(`Missing required field: ${field}`);
      }
    }

    this.connections.set(connectorId, {
      ...credentials,
      connectedAt: Date.now(),
      status: 'connected'
    });

    this.saveConnections();
    return true;
  }

  /**
   * Remove a connection
   */
  removeConnection(connectorId) {
    this.connections.delete(connectorId);
    this.saveConnections();
  }

  /**
   * Get connection credentials
   */
  getConnection(connectorId) {
    return this.connections.get(connectorId);
  }

  /**
   * Check if connector is connected
   */
  isConnected(connectorId) {
    return this.connections.has(connectorId);
  }

  /**
   * Get all active connections
   */
  getAllConnections() {
    return Array.from(this.connections.entries()).map(([id, creds]) => ({
      id,
      ...CONNECTOR_TYPES[id.toUpperCase()],
      credentials: creds,
      connected: true
    }));
  }

  /**
   * Test connection
   */
  async testConnection(connectorId) {
    const connector = CONNECTOR_TYPES[connectorId.toUpperCase()];
    const credentials = this.getConnection(connectorId);

    if (!connector || !credentials) {
      return { success: false, error: 'Not configured' };
    }

    // Test based on connector type
    try {
      switch (connectorId) {
        case 'github':
          const response = await fetch('https://api.github.com/user', {
            headers: { 'Authorization': `token ${credentials.apiToken}` }
          });
          return { success: response.ok, status: response.status };

        case 'openai':
          const openaiTest = await fetch('https://api.openai.com/v1/models', {
            headers: { 'Authorization': `Bearer ${credentials.apiKey}` }
          });
          return { success: openaiTest.ok, status: openaiTest.status };

        default:
          return { success: true, message: 'Configuration saved (test not implemented)' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}

// Singleton instance
let connectorManager = null;

export function getConnectorManager() {
  if (!connectorManager) {
    connectorManager = new ConnectorManager();
  }
  return connectorManager;
}

export default ConnectorManager;
